import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useAudio } from '../hooks/useAudio'
import { type Trash, trashTypes } from '../interfaces/GameScreen'

export const isTouchDevice = () => {
	return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

type Screens = 'home' | 'instructions' | 'game' | 'gameOver' | 'timeUp' | 'ranking'

interface GameData {
	score: number
	lives: number
	matchTime: number
}

interface GameContextType {
	currentScreen: Screens
	trashItems: Trash[]
	selectedTrash: Trash | null
	gameData: GameData
	changeScreen: (screen: Screens) => void
	setGameData: React.Dispatch<React.SetStateAction<GameData>>
	spawnNewTrash: (removeId?: string | null) => void
	handleTrashAnimationEnd: (item: Trash) => void
	handleTrashSelect: (item: Trash) => void
	handleBinClick: (binId: string) => void
	handleDrop: (item: Trash, binId: string) => void
	resetGame: () => void
}

const GameContext = createContext<GameContextType | undefined>(undefined)

export function GameProvider({ children }: { children: ReactNode }) {
	const [currentScreen, setCurrentScreen] = useState<Screens>('home')
	const [trashItems, setTrashItems] = useState<Trash[]>([])
	const [selectedTrash, setSelectedTrash] = useState<Trash | null>(null)
	const [matchStartTime, setMatchStartTime] = useState<number>(0)
	const [gameData, setGameData] = useState<GameData>({
		score: 0,
		lives: 5,
		matchTime: 0,
	})
	const { playCorrect, playWrong } = useAudio()

	const spawnNewTrash = (removeId: string | null = null) => {
		setTrashItems((prev) => {
			const filteredItems = removeId ? prev.filter((trash) => trash.id !== removeId) : prev

			const targetCount = Math.min(6, Math.max(2, filteredItems.length + 1))
			const itemsToAdd = targetCount - filteredItems.length

			if (itemsToAdd <= 0) return filteredItems

			const newItems = []

			for (let i = 0; i < itemsToAdd; i++) {
				const randomTrash = trashTypes[Math.floor(Math.random() * trashTypes.length)]

				const newItem = {
					...randomTrash,
					id: uuidv4(),
					y: window.innerHeight * 0.6 + Math.random() * (window.innerHeight * 0.4 - 100),
				}

				newItems.push(newItem)
			}

			return [...filteredItems, ...newItems]
		})
		//
		//setTrashItems((prev) => {
		//	const filteredItems = removeId ? prev.filter((trash) => trash.id !== removeId) : prev
		//
		//	if (filteredItems.length > 5) return filteredItems
		//
		//	const randomTrash = trashTypes[Math.floor(Math.random() * trashTypes.length)]
		//	const newItem = {
		//		...randomTrash,
		//		id: uuidv4(),
		//		y: window.innerHeight * 0.6 + Math.random() * (window.innerHeight * 0.4 - 100),
		//	}
		//
		//	return [...filteredItems, newItem]
		//})
	}

	const handleTrashAnimationEnd = (item: Trash) => {
		spawnNewTrash(item.id)
	}

	const handleTrashSelect = (item: Trash) => {
		setSelectedTrash(selectedTrash?.id === item.id ? null : item)
	}

	const handleBinClick = (binId: string) => {
		if (selectedTrash) {
			handleDrop(selectedTrash, binId)
			setSelectedTrash(null)
		}
	}

	const handleDrop = (item: Trash, binId: string) => {
		const isCorrect = item.bin === binId

		if (isCorrect) {
			playCorrect()
		} else {
			playWrong()
		}

		setGameData((prev) => {
			const newScore = isCorrect ? prev.score + item.points : Math.max(0, prev.score - 5)
			const newLives = isCorrect ? prev.lives : prev.lives - 1

			if (!isCorrect && newLives <= 0) {
				const matchTime = Math.floor((Date.now() - matchStartTime) / 1000)
				setTimeout(() => setCurrentScreen('gameOver'), 100)

				return { lives: newLives, score: newScore, matchTime }
			}

			return { score: newScore, lives: newLives, matchTime: 0 }
		})

		spawnNewTrash(item.id)
	}

	const resetGame = () => {
		setGameData({ score: 0, lives: 5, matchTime: 0 })
		setTrashItems([])
		setSelectedTrash(null)
	}

	function changeScreen(screen: Screens) {
		setCurrentScreen(screen)
	}

	useEffect(() => {
		if (currentScreen === 'game') {
			resetGame()
			spawnNewTrash()
			setMatchStartTime(Date.now())

			// Spawn automático a cada 3-5 segundos para garantir fluxo constante
			const spawnInterval = setInterval(
				() => {
					spawnNewTrash()
				},
				3000 + Math.random() * 2000
			) // 3-5 segundos aleatório

			return () => clearInterval(spawnInterval)
		}
	}, [currentScreen])

	return (
		<GameContext.Provider
			value={{
				currentScreen,
				changeScreen,
				trashItems,
				selectedTrash,
				gameData,
				setGameData,
				spawnNewTrash,
				handleTrashAnimationEnd,
				handleTrashSelect,
				handleBinClick,
				handleDrop,
				resetGame,
			}}
		>
			{children}
		</GameContext.Provider>
	)
}

export function useGame() {
	const context = useContext(GameContext)
	if (context === undefined) {
		throw new Error('useGame must be used within a GameProvider')
	}
	return context
}
